import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  IoNotifications,
  IoNotificationsOffOutline,
  IoClose,
  IoInformationCircleOutline,
} from "react-icons/io5";
import api from "@/api";
import { tapLight, tapSuccess } from "@/utils/haptics";

/**
 * Sheet that lets the user:
 *  1) enable / disable Web Push subscription on this device,
 *  2) toggle individual notification categories (persisted per-user via the
 *     backend so all your devices share the same filters).
 *
 * `pushActive` (from parent) reflects whether the browser is currently subscribed.
 * `onToggleSubscription` — parent handler that (un)subscribes and refreshes the state.
 */
export const NotificationPrefsDialog = ({
  open,
  onOpenChange,
  pushActive,
  pushSupported,
  standalone,
  toggleBusy,
  onToggleSubscription,
}) => {
  const [categories, setCategories] = useState([]);
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [catsRes, prefsRes] = await Promise.all([
          api.get("/notifications/categories"),
          api.get("/notifications/prefs"),
        ]);
        if (!alive) return;
        setCategories(catsRes.data?.categories || []);
        setPrefs(prefsRes.data?.prefs || {});
        setDirty(false);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Impossibile caricare le preferenze");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [open]);

  const togglePref = (key) => {
    setPrefs((p) => ({ ...p, [key]: !p[key] }));
    setDirty(true);
    tapLight();
  };

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/notifications/prefs", { prefs });
      setPrefs(data?.prefs || prefs);
      setDirty(false);
      tapSuccess();
      toast.success("Preferenze salvate");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore nel salvataggio");
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const disabledPref = !pushActive; // If push is off, category toggles are irrelevant

  return (
    <div
      className="tf-sheet-backdrop"
      data-testid="notif-prefs-backdrop"
      onClick={() => onOpenChange(false)}
      style={{ zIndex: 80 }}
    >
      <div
        data-testid="notif-prefs-dialog"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "18px 18px 0 0",
          width: "100%",
          maxWidth: 480,
          maxHeight: "85vh",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          boxShadow: "0 -6px 24px rgba(0,0,0,0.15)",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            padding: "16px 18px",
            borderBottom: "1px solid #e5e7eb",
            flexShrink: 0,
          }}
        >
          <div style={{ fontWeight: 800, fontSize: 17 }}>Notifiche</div>
          <button
            type="button"
            data-testid="notif-prefs-close"
            onClick={() => onOpenChange(false)}
            aria-label="Chiudi"
            style={{ background: "transparent", border: "none", padding: 4, cursor: "pointer" }}
          >
            <IoClose size={22} />
          </button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: "auto", padding: "12px 18px 12px", flex: 1 }}>
          {/* Master switch */}
          <div
            style={{
              display: "flex",
              gap: 12,
              alignItems: "center",
              padding: 14,
              background: pushActive ? "#f0fdf4" : "#f9fafb",
              border: `1px solid ${pushActive ? "#bbf7d0" : "#e5e7eb"}`,
              borderRadius: 12,
              marginBottom: 14,
            }}
          >
            <div
              style={{
                width: 40, height: 40, borderRadius: 10, flexShrink: 0,
                background: pushActive ? "#16a34a" : "#9ca3af",
                color: "#fff",
                display: "flex", alignItems: "center", justifyContent: "center",
              }}
            >
              {pushActive ? <IoNotifications size={22} /> : <IoNotificationsOffOutline size={22} />}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {pushActive ? "Notifiche attive su questo dispositivo" : "Notifiche disattivate"}
              </div>
              <div className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 2, lineHeight: 1.35 }}>
                {!pushSupported
                  ? "Il tuo browser non supporta le notifiche."
                  : !standalone
                    ? "Installa l'app sul telefono per attivare le notifiche."
                    : pushActive
                      ? "Puoi decidere qui sotto quali categorie ricevere."
                      : "Attiva le notifiche per personalizzare le categorie."}
              </div>
            </div>
            <button
              type="button"
              data-testid="notif-prefs-master-toggle"
              disabled={toggleBusy || !pushSupported || !standalone}
              onClick={onToggleSubscription}
              style={{
                background: pushActive ? "#0a0a0a" : "#E2001A",
                color: "#fff",
                border: "none",
                borderRadius: 999,
                padding: "8px 14px",
                fontSize: 12,
                fontWeight: 700,
                cursor: toggleBusy ? "not-allowed" : "pointer",
                opacity: toggleBusy || !pushSupported || !standalone ? 0.5 : 1,
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              {pushActive ? "Disattiva" : "Attiva"}
            </button>
          </div>

          {/* Info banner about opt-out model */}
          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "flex-start",
              padding: 12,
              background: "#fefce8",
              border: "1px solid #fde68a",
              borderRadius: 10,
              marginBottom: 14,
              fontSize: 12,
              lineHeight: 1.4,
              color: "#78350f",
            }}
          >
            <IoInformationCircleOutline size={16} style={{ flexShrink: 0, marginTop: 1 }} />
            <div>
              Ogni categoria mostra chi la riceve. Le impostazioni valgono per tutti i tuoi dispositivi.
            </div>
          </div>

          {/* Category toggles */}
          {loading ? (
            <div style={{ padding: 24, textAlign: "center", color: "var(--tf-muted)", fontSize: 13 }}>
              Caricamento…
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }} data-testid="notif-prefs-categories">
              {categories.map((cat) => {
                const enabled = !!prefs[cat.key];
                return (
                  <label
                    key={cat.key}
                    data-testid={`notif-pref-row-${cat.key}`}
                    style={{
                      display: "flex",
                      gap: 12,
                      alignItems: "flex-start",
                      padding: 12,
                      background: "#fff",
                      border: "1px solid #e5e7eb",
                      borderRadius: 12,
                      cursor: disabledPref ? "not-allowed" : "pointer",
                      opacity: disabledPref ? 0.5 : 1,
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{cat.label_it}</div>
                      <div className="tf-small" style={{ color: "var(--tf-muted)", marginTop: 4, lineHeight: 1.4 }}>
                        {cat.desc_it}
                      </div>
                    </div>
                    {/* iOS-style switch (pure CSS) */}
                    <span
                      style={{
                        position: "relative",
                        width: 46, height: 26, flexShrink: 0,
                        background: enabled ? "#16a34a" : "#d1d5db",
                        borderRadius: 999,
                        transition: "background 0.18s ease",
                      }}
                    >
                      <input
                        type="checkbox"
                        data-testid={`notif-pref-switch-${cat.key}`}
                        checked={enabled}
                        disabled={disabledPref}
                        onChange={() => !disabledPref && togglePref(cat.key)}
                        style={{
                          position: "absolute",
                          inset: 0,
                          opacity: 0,
                          cursor: disabledPref ? "not-allowed" : "pointer",
                          margin: 0,
                        }}
                      />
                      <span
                        style={{
                          position: "absolute",
                          top: 3,
                          left: enabled ? 23 : 3,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          background: "#fff",
                          boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                          transition: "left 0.18s ease",
                        }}
                      />
                    </span>
                  </label>
                );
              })}
            </div>
          )}
        </div>

        {/* Sticky footer with Save button */}
        <div
          style={{
            padding: "12px 18px",
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            gap: 8,
            flexShrink: 0,
            background: "#fff",
          }}
        >
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            style={{
              flex: 1,
              padding: "12px 16px",
              background: "transparent",
              color: "#0a0a0a",
              border: "1.5px solid #0a0a0a",
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Chiudi
          </button>
          <button
            type="button"
            data-testid="notif-prefs-save"
            onClick={save}
            disabled={saving || !dirty}
            style={{
              flex: 1,
              padding: "12px 16px",
              background: dirty && !saving ? "#E2001A" : "#9ca3af",
              color: "#fff",
              border: "none",
              borderRadius: 12,
              fontSize: 14,
              fontWeight: 700,
              cursor: saving || !dirty ? "not-allowed" : "pointer",
            }}
          >
            {saving ? "Salvo…" : dirty ? "Salva" : "Salvato"}
          </button>
        </div>
      </div>
    </div>
  );
};
